import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ClientDashboardOverviewPage from './ClientDashboardOverviewPage';
import { dashboardApi, companiesApi, myOrdersApi, paymentsApi, dscConsentApi } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  FileText, LogOut, Loader2, Clock, CheckCircle, CreditCard, Upload,
  PenTool, Eye, AlertTriangle, ChevronRight, Shield
} from 'lucide-react';
import { companyInfo } from '../data/mock';
import NotificationBell from '../components/NotificationBell';
import {
  PORTAL_NAV, DashboardOverview, CompaniesList, OrdersSection,
  DocVaultSection, DataRoomSection, InvoicesSection,
} from '../components/client/PortalSections';
import { TicketsSection } from '../components/client/TicketsSection';
import CompanyDetailView from '../components/client/CompanyDetailView';

const ORDER_STATUS_CONFIG = {
  draft: { label: 'Pay Now', color: 'bg-emerald-500/20 text-emerald-400', icon: CreditCard },
  invoice_requested: { label: 'Invoice Requested', color: 'bg-blue-500/20 text-blue-400', icon: FileText },
  payment_pending: { label: 'Payment Pending', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  payment_received: { label: 'Payment Received', color: 'bg-teal-500/20 text-teal-400', icon: CheckCircle },
  payment_complete: { label: 'Paid', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle },
  documents_pending: { label: 'Upload Docs', color: 'bg-orange-500/20 text-orange-400', icon: Upload },
  pending_documents: { label: 'Upload Docs', color: 'bg-orange-500/20 text-orange-400', icon: Upload },
  under_review: { label: 'Under Review', color: 'bg-purple-500/20 text-purple-400', icon: Eye },
  signature_pending: { label: 'Sign Documents', color: 'bg-cyan-500/20 text-cyan-400', icon: PenTool },
  signature_review: { label: 'Signature Review', color: 'bg-indigo-500/20 text-indigo-400', icon: Eye },
  completed: { label: 'Completed', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: FileText },
};

const MONTH_NAMES = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

// Filter out demo / placeholder / cancelled orders from client views
const isClientVisibleOrder = (o) => {
  if (!o) return false;
  const status = String(o.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled') return false;
  const rawName = String(o.display_name || o.product?.name || '').trim();
  if (!rawName) return false;
  const lower = rawName.toLowerCase();
  // Placeholder patterns: "GST March", "GST April", "GST filings", "GST" alone
  if (lower === 'gst' || lower === 'gst filings' || lower === 'gst filing') return false;
  if (MONTH_NAMES.some((m) => lower === `gst ${m}`)) return false;
  // Auto-generated fallback names: "Order", "Order #abc123", "Order 1234"
  if (/^order(\s*#?\s*[a-z0-9]+)?$/i.test(rawName)) return false;
  return true;
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDscConsents, setPendingDscConsents] = useState([]);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  useEffect(() => {
    fetchData();
    // Handle payment return
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    if (sessionId && paymentStatus === 'success') {
      (async () => {
        try {
          const status = await paymentsApi.getStripePaymentStatus(sessionId);
          if (status.payment_status === 'paid') {
            toast.success('Payment successful! You can now submit documents.');
            fetchData();
          } else {
            toast.info('Payment is being processed...');
          }
        } catch (error) { console.error('Error checking payment:', error); }
        window.history.replaceState({}, '', window.location.pathname);
      })();
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // React to ?section= param changes (also fires on initial mount).
  // Lets buttons like "Check all orders" navigate to /dashboard?section=orders
  // even when the user is already on /dashboard.
  useEffect(() => {
    const section = new URLSearchParams(location.search).get('section');
    if (section) setActiveSection(section);
  }, [location.search]);

  const fetchData = async () => {
    try {
      const [statsData, companiesData, ordersData] = await Promise.all([
        dashboardApi.getStats(),
        companiesApi.getAll(),
        myOrdersApi.getAll(),
      ]);
      setStats(statsData);
      setCompanies(companiesData);
      // Hide cancelled + demo/placeholder orders from client-facing surfaces
      setOrders((ordersData || []).filter(isClientVisibleOrder));
      try {
        const dscData = await dscConsentApi.getPending();
        setPendingDscConsents(dscData);
      } catch { setPendingDscConsents([]); }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  const getStatusConfig = (status) => ORDER_STATUS_CONFIG[status] || { label: status, color: 'bg-slate-700 text-slate-400', icon: FileText };
  const getOrderStatusDisplay = (order) => {
    const terminal = ['completed', 'cancelled', 'canceled'].includes(String(order?.status || '').toLowerCase());
    if (order?.has_rejected_documents && !terminal) {
      return { label: 'Action Required — Re-upload Needed', color: 'bg-amber-500/20 text-amber-300 border border-amber-500/40', icon: AlertTriangle };
    }
    return getStatusConfig(order?.status);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const handleSelectSection = (id) => {
    setActiveSection(id);
    setSelectedCompanyId(null);
  };
  const handleSelectCompany = (companyId) => {
    setSelectedCompanyId(companyId);
    setActiveSection('companies');
  };

  const upcomingDeadlines = orders
    .filter((o) => o.days_remaining != null && o.days_remaining <= 45 && o.status !== 'completed' && o.status !== 'cancelled')
    .sort((a, b) => (a.days_remaining || 999) - (b.days_remaining || 999));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f0d] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f0d] text-white" data-testid="client-dashboard">
      {/* Top Header */}
      <header className="bg-[#0f1611] border-b border-emerald-900/30 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={companyInfo.logo} alt={companyInfo.name} className="w-8 h-8" />
              <span className="text-lg font-serif text-white hidden sm:inline">{companyInfo.name}</span>
            </div>
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="text-xs sm:text-sm text-slate-400 hidden md:inline" data-testid="header-user-email">{user?.email}</span>
              <NotificationBell />
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-slate-800 px-2 sm:px-3" data-testid="logout-btn">
                <LogOut className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Log out</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8 flex gap-6 lg:gap-8">
        {/* Left Sidebar (desktop) */}
        <aside className="hidden md:block w-60 flex-shrink-0" data-testid="portal-sidebar">
          <nav className="sticky top-24 space-y-1.5">
            {PORTAL_NAV.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSelectSection(item.id)}
                data-testid={`portal-nav-${item.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-[15px] font-medium transition-all ${
                  activeSection === item.id
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 shadow-sm shadow-emerald-500/10'
                    : 'text-slate-400 hover:text-white hover:bg-white/[0.02] border border-transparent'
                }`}
              >
                <item.icon className="w-5 h-5" /> {item.label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          {/* Mobile Nav */}
          <div className="md:hidden flex gap-1 overflow-x-auto mb-4 pb-1 -mx-3 px-3" data-testid="portal-mobile-nav">
            {PORTAL_NAV.map((item) => (
              <button key={item.id} onClick={() => handleSelectSection(item.id)} data-testid={`portal-mobile-nav-${item.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  activeSection === item.id ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[#0f1611] text-slate-400 border border-emerald-900/30'
                }`}>
                {item.label}
              </button>
            ))}
          </div>

          {/* Global alerts (only on Dashboard section) */}
          {activeSection === 'dashboard' && upcomingDeadlines.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-6" data-testid="upcoming-deadlines">
              <h3 className="text-base font-serif text-amber-400 flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5" />
                Upcoming Document Deadlines ({upcomingDeadlines.length})
              </h3>
              <div className="space-y-2">
                {upcomingDeadlines.slice(0, 5).map((order) => (
                  <Link key={order.id} to={`/orders/${order.id}`}
                    className="flex items-center justify-between bg-[#0f1611] hover:bg-emerald-500/5 border border-emerald-900/30 rounded-lg p-3 transition-colors"
                    data-testid={`deadline-order-${order.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{order.display_name || order.product?.name || 'Service'}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{order.company?.company_name} · Due: {order.deadline_formatted}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 ml-3" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'dashboard' && pendingDscConsents.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-6" data-testid="pending-dsc-consents">
              <h3 className="text-base font-serif text-amber-400 flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5" /> Pending DSC Consent Requests ({pendingDscConsents.length})
              </h3>
              <div className="space-y-2">
                {pendingDscConsents.map((consent) => (
                  <Link key={consent.id} to={`/orders/${consent.order_id}`}
                    className="flex items-center justify-between bg-[#0f1611] hover:bg-emerald-500/5 border border-emerald-900/30 rounded-lg p-3 transition-colors"
                    data-testid={`dsc-consent-card-${consent.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{consent.order_name}</p>
                      <p className="text-xs text-slate-400">Requested by {consent.admin_name}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400 ml-3">Pending</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Section content */}
          {activeSection === 'dashboard' && (
            <ClientDashboardOverviewPage
              onNavigateSection={handleSelectSection}
              companiesSlot={
                <CompaniesList
                  companies={companies}
                  orders={orders}
                  onSelect={handleSelectCompany}
                  onAddCompany={() => navigate('/onboarding')}
                />
              }
            />
          )}

          {activeSection === 'companies' && (
            selectedCompanyId ? (
              <CompanyDetailView
                companyId={selectedCompanyId}
                orders={orders}
                userRole={user?.role}
                onBack={() => setSelectedCompanyId(null)}
              />
            ) : (
              <CompaniesList
                companies={companies}
                orders={orders}
                onSelect={handleSelectCompany}
                onAddCompany={() => navigate('/onboarding')}
              />
            )
          )}

          {activeSection === 'orders' && (
            <OrdersSection orders={orders} companies={companies} getOrderStatusDisplay={getOrderStatusDisplay} formatCurrency={formatCurrency} />
          )}
          {activeSection === 'docvault' && <DocVaultSection />}
          {activeSection === 'dataroom' && <DataRoomSection />}
          {activeSection === 'invoices' && <InvoicesSection />}
          {activeSection === 'tickets' && (
            <TicketsSection
              onNavigateToCompany={(company) => {
                if (company?.id) {
                  setSelectedCompanyId(company.id);
                  setActiveSection('companies');
                }
              }}
              onNavigateToOrder={(orderId) => {
                if (orderId) navigate(`/orders/${orderId}`);
              }}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
